import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { getPrintPackageItemListingLabel } from '../../AlbumPrintPackageOption/helpers';
import { sortPrintPackageItems } from '../../../../../../../../../../util/print-packages';
import { formatPriceForDisplay } from '../../../../../../../../../../util/format-money';
import { getAlbumPrintPackageTaggedItem } from '../../../helpers';
import CustomControl from '../../../../../../../../../../components/global/CustomControl';

const propTypes = {
  className: PropTypes.string,
  albumPrintPackage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_package: PropTypes.shape({
      items: PropTypes.shape({
        total: PropTypes.number.isRequired,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            is_optional: PropTypes.bool,
            type: PropTypes.shape({
              name: PropTypes.string.isRequired,
            }),
            custom_print: PropTypes.shape({
              id: PropTypes.string.isRequired,
              name: PropTypes.string.isRequired,
            }),
            print_product_configuration: PropTypes.shape({
              print_product: PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
              }).isRequired,
              print_product_size: PropTypes.shape({
                id: PropTypes.string.isRequired,
                print_size: PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string.isRequired,
                }).isRequired,
              }).isRequired,
            }),
          }).isRequired,
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  selectedPrintPackageItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ).isRequired,
  onPrintPackageItemToggle: PropTypes.func.isRequired,
  cartItem: PropTypes.object.isRequired,
};

const SelectablePrintPackageItemsList = ({
  className,
  albumPrintPackage,
  selectedPrintPackageItems,
  onPrintPackageItemToggle,
  cartItem,
}) => {
  const printPackage = albumPrintPackage.print_package;
  const printPackageItems = sortPrintPackageItems(printPackage.items.records);

  // Remove all optional items from the list as we don't want to show them.
  const filteredPrintPackageItems = printPackageItems.filter(
    printPackageItem =>
      printPackageItem.album_folder_print_package_items.total === 0,
  );
  const overriddenPrintPackageItem = getAlbumPrintPackageTaggedItem(
    albumPrintPackage,
  );

  const albumFolderTag = overriddenPrintPackageItem?.album_folder_tag;
  const price = overriddenPrintPackageItem?.price?.value ?? 0;
  const discountPrice = overriddenPrintPackageItem?.discount_price?.value ?? 0;
  const finalPrice = discountPrice || price;
  const classes = classNames({
    [className]: !!className,
    'selectable-album-print-package-option-items': true,
  });

  return (
    <div className={classes}>
      {filteredPrintPackageItems.map(printPackageItem => {
        const isSelected = selectedPrintPackageItems.find(
          item => item.id === printPackageItem.id,
        );

        return (
          <CustomControl
            id={`${albumPrintPackage.id}-${printPackageItem.id}`}
            type="checkbox"
            shape="squared"
            className="d-block"
            size="sm"
            label={getPrintPackageItemListingLabel(printPackageItem)}
            disabled={
              !!cartItem ||
              (!isSelected &&
                selectedPrintPackageItems.length ===
                  printPackage.max_checkout_items)
            }
            checked={isSelected}
            onChange={() => {
              onPrintPackageItemToggle(printPackageItem);
            }}
          />
        );
      })}

      {
        // TODO check what happens when this case is active
        overriddenPrintPackageItem && (
          <Fragment>
            {overriddenPrintPackageItem.is_optional ? (
              <li>
                <strong>
                  Add{' '}
                  {pluralize(
                    albumFolderTag?.name ?? '',
                    overriddenPrintPackageItem.quantity,
                  )}{' '}
                  {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}{' '}
                  for ${formatPriceForDisplay(finalPrice)}
                </strong>
              </li>
            ) : (
              <li>
                <strong>
                  Includes{' '}
                  {pluralize(
                    albumFolderTag?.name ?? '',
                    overriddenPrintPackageItem.quantity,
                  )}{' '}
                  {/* Since items from both sides are needed, allow the albumFolderPrintPackage to override */}
                  {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}
                </strong>
              </li>
            )}
          </Fragment>
        )
      }
    </div>
  );
};

SelectablePrintPackageItemsList.propTypes = propTypes;

export default SelectablePrintPackageItemsList;
